import nipplejs from 'nipplejs';

// Touch backend for InputManager. One steer joystick on the left, button
// stack on the right (jump, tuck, item, trick A/B/C — tricks only show
// while airborne, which the InputManager toggles via setTricksAvailable).
export class TouchControls {
  constructor(manager) {
    this.manager = manager;
    this.enabled = false;
    this.joystick = null;

    this._bound = {
      jumpStart: (e) => { e.preventDefault(); this.manager.jumpDown(); },
      jumpEnd: (e) => { e.preventDefault(); this.manager.jumpUp(); },
      itemStart: (e) => this._buttonPress(e, () => this.manager.pressItem()),
      tuckStart: (e) => this._tuckStart(e),
      tuckEnd: (e) => this._tuckEnd(e),
    };
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;

    const zone = document.getElementById('steer-joystick');
    if (zone) {
      // Sanity check: nipplejs measures the zone's bounds at create-time
      // and won't recover if they're zero. CSS bugs (display:none on a
      // parent, or anything else that makes the zone collapse) have broken
      // the joystick multiple times — log loudly so it's obvious.
      const rect = zone.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        console.warn(
          'TouchControls: #steer-joystick has zero bounds at create-time ' +
          '(width=%d height=%d). The joystick will not register touches. ' +
          'Make sure body.mobile is applied and no ancestor is display:none ' +
          'before TouchControls.enable() runs.',
          rect.width, rect.height,
        );
      }

      // Matches the web-arena-game's working setup (no lockX, no curve)
      // since both quirks were causing the joystick to feel broken on
      // mobile.
      this.joystick = nipplejs.create({
        zone,
        mode: 'static',
        position: { left: '50%', top: '50%' },
        color: 'rgba(255, 255, 255, 0.5)',
        size: 100,
      });

      this.joystick.on('move', (_evt, data) => {
        // Guard against nipplejs firing 'move' with incomplete data and
        // against the touch landing while we're not enabled.
        if (!data || !data.direction || !this.enabled) return;
        // angle 0 = right, PI/2 = up. Only the horizontal axis steers.
        const x = Math.cos(data.angle.radian) * Math.min(data.force, 1);
        this.manager.setSteer(x);
      });
      this.joystick.on('end', () => this.manager.setSteer(0));
    }

    // 2-finger horizontal drag orbits the camera around the racer (without
    // turning the racer). Lets the player look around / look back.
    this._setupTwoFingerPan();

    this._bind('btn-jump', 'touchstart', this._bound.jumpStart);
    this._bind('btn-jump', 'touchend', this._bound.jumpEnd);
    this._bind('btn-jump', 'touchcancel', this._bound.jumpEnd);
    this._bind('btn-item', 'touchstart', this._bound.itemStart);
    this._bind('btn-tuck', 'touchstart', this._bound.tuckStart);
    this._bind('btn-tuck', 'touchend', this._bound.tuckEnd);
    this._bind('btn-tuck', 'touchcancel', this._bound.tuckEnd);
  }

  _setupTwoFingerPan() {
    this._panBaseAngle = 0;     // initial camera offset when the gesture began
    this._panBaseTouchAngle = 0; // angle of the two-finger axis at start
    this._panActiveTouches = []; // [{identifier, x, y}, ...] when 2 fingers down

    this._boundPanStart = (e) => {
      // Only kick in when exactly two fingers are now down.
      if (e.touches.length !== 2) return;
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      // Skip if either finger landed on the joystick or a button — those
      // are gameplay inputs and the pan gesture mustn't fight them.
      if (this._touchIsOnUI(t0.target) || this._touchIsOnUI(t1.target)) return;
      this._panActiveTouches = [
        { id: t0.identifier, x: t0.clientX, y: t0.clientY },
        { id: t1.identifier, x: t1.clientX, y: t1.clientY },
      ];
      this._panBaseAngle = this.manager.getViewYawOffset();
      this._panBaseTouchAngle = Math.atan2(t1.clientY - t0.clientY, t1.clientX - t0.clientX);
      this.manager.setPanActive(true);
    };
    this._boundPanMove = (e) => {
      if (this._panActiveTouches.length !== 2 || e.touches.length < 2) return;
      // Find the same two identifiers in the current touch list.
      const a = this._findTouch(e.touches, this._panActiveTouches[0].id);
      const b = this._findTouch(e.touches, this._panActiveTouches[1].id);
      if (!a || !b) return;
      const cur = Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
      let delta = cur - this._panBaseTouchAngle;
      // Normalise to (-π, π].
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      // Use horizontal centroid drag too for a more intuitive feel: average
      // the two fingers' X delta and add it to the yaw offset.
      const a0 = this._panActiveTouches[0];
      const b0 = this._panActiveTouches[1];
      const startCenterX = (a0.x + b0.x) / 2;
      const curCenterX = (a.clientX + b.clientX) / 2;
      const dx = curCenterX - startCenterX;
      // ~300 px of horizontal centroid drag = π radians camera orbit.
      const offset = this._panBaseAngle + delta + (dx / 300) * Math.PI;
      this.manager.setViewYawOffset(offset);
    };
    this._boundPanEnd = (e) => {
      // End the gesture when fewer than 2 fingers remain.
      if (e.touches.length < 2 && this._panActiveTouches.length === 2) {
        this._panActiveTouches = [];
        this.manager.setPanActive(false);
      }
    };

    window.addEventListener('touchstart', this._boundPanStart, { passive: true });
    window.addEventListener('touchmove', this._boundPanMove, { passive: true });
    window.addEventListener('touchend', this._boundPanEnd, { passive: true });
    window.addEventListener('touchcancel', this._boundPanEnd, { passive: true });
  }

  _teardownTwoFingerPan() {
    if (!this._boundPanStart) return;
    window.removeEventListener('touchstart', this._boundPanStart);
    window.removeEventListener('touchmove', this._boundPanMove);
    window.removeEventListener('touchend', this._boundPanEnd);
    window.removeEventListener('touchcancel', this._boundPanEnd);
    this._boundPanStart = null;
  }

  _findTouch(list, id) {
    for (const t of list) if (t.identifier === id) return t;
    return null;
  }

  _touchIsOnUI(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('#steer-joystick, #btn-stack, .touch-btn, button, select');
  }

  disable() {
    if (!this.enabled) return;
    this.enabled = false;
    this._teardownTwoFingerPan();

    if (this.joystick) {
      this.joystick.destroy();
      this.joystick = null;
    }
    this._unbind('btn-jump', 'touchstart', this._bound.jumpStart);
    this._unbind('btn-jump', 'touchend', this._bound.jumpEnd);
    this._unbind('btn-jump', 'touchcancel', this._bound.jumpEnd);
    this._unbind('btn-item', 'touchstart', this._bound.itemStart);
    this._unbind('btn-tuck', 'touchstart', this._bound.tuckStart);
    this._unbind('btn-tuck', 'touchend', this._bound.tuckEnd);
    this._unbind('btn-tuck', 'touchcancel', this._bound.tuckEnd);
  }

  _bind(id, evt, fn) {
    const el = document.getElementById(id);
    if (el) el.addEventListener(evt, fn, { passive: false });
  }
  _unbind(id, evt, fn) {
    const el = document.getElementById(id);
    if (el) el.removeEventListener(evt, fn);
  }

  _buttonPress(e, action) {
    e.preventDefault();
    action();
  }
  _tuckStart(e) {
    e.preventDefault();
    this.manager.setTucking(true);
  }
  _tuckEnd(e) {
    e.preventDefault();
    this.manager.setTucking(false);
  }
}
